/**
 * LiveMessagesPanel — Iter-170 · Phase 2.
 *
 * Two-tab staff comms surface. Tab 1 ("General / Regional") broadcasts
 * across the current user's region; Tab 2 ("Crew / Daily") is scoped
 * to the current crew + today's route date. Anyone can post; mentions
 * (@name) generate a distinct notification server-side.
 *
 * Consumes /api/staff-messages/{general,crew,unread-count,read-all}.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { AtSign, MessageSquare, Send, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const same = new Date().toDateString() === d.toDateString();
  return same ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function MessageRow({ m, onAck, canAck }) {
  const scope = m.channel === 'crew'
    ? (m.crew_id ? `Crew ${m.crew_id.slice(-4)} · ${m.route_date || 'today'}` : 'Crew')
    : (m.region_name || 'Company');
  const isSystem = !!m.system;
  const alreadyAcked = !!m.ack_on_the_way_at;
  return (
    <div className={`border-b border-zinc-800 py-3 ${isSystem ? 'bg-[#0b0b0b] px-2 rounded-md' : ''}`}
         data-testid="staff-message-row"
         data-system={isSystem || undefined}>
      <div className="flex items-center gap-2 flex-wrap text-xs mb-1">
        {isSystem ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#C9A962] text-black text-[10px] font-bold uppercase tracking-widest"
                data-testid="staff-message-system-tag">
            System
          </span>
        ) : (
          <>
            <span className="font-bold text-white">{m.author_name}</span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-400">{m.author_role}</span>
          </>
        )}
        <span className="text-zinc-500">·</span>
        <span className="text-[#C9A962] font-semibold">{scope}</span>
        <span className="ml-auto text-zinc-500 text-[10px]">{fmt(m.created_at)}</span>
      </div>
      <p className="text-sm text-zinc-200 leading-snug whitespace-pre-wrap break-words">{m.body}</p>
      {Array.isArray(m.mentions) && m.mentions.length > 0 && (
        <p className="mt-1 text-[10px] text-blue-400 flex items-center gap-1">
          <AtSign className="h-3 w-3" /> {m.mentions.map((mm) => mm.name).join(', ')}
        </p>
      )}
      {isSystem && (
        <div className="mt-2 flex items-center gap-2">
          {alreadyAcked ? (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-600 text-white"
                  data-testid={`staff-message-${m.message_id}-status`}>
              In Progress · {m.ack_on_the_way_by_name || 'Crew'}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onAck(m.message_id)}
              disabled={!canAck}
              className="px-3 py-1 rounded-full text-[11px] font-bold bg-[#C9A962] text-black hover:bg-[#B8974E] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`staff-message-${m.message_id}-ack`}
            >
              On the way
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Composer({ onSend, placeholder }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await onSend(body.trim());
      setBody('');
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex items-end gap-2" data-testid="staff-message-composer">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="flex-1 bg-[#0b0b0b] text-white border-zinc-800 text-sm resize-y"
        data-testid="staff-message-input"
      />
      <Button
        onClick={submit}
        disabled={busy || !body.trim()}
        className="h-10 px-3 bg-[#C9A962] text-black hover:bg-[#B8974E] font-bold"
        data-testid="staff-message-send"
      >
        <Send className="h-4 w-4 mr-1.5" /> Post
      </Button>
    </div>
  );
}

export default function LiveMessagesPanel({ user }) {
  const [active, setActive] = useState('general');
  const [general, setGeneral] = useState([]);
  const [crew, setCrew] = useState([]);
  const [counts, setCounts] = useState({ general: 0, crew: 0, total: 0 });
  // Iter-175 · Phase 4 — visible inline error state replaces silent
  // swallowing on load/post failures. `null` when healthy.
  const [loadError, setLoadError] = useState(null);
  const [sendError, setSendError] = useState(null);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const loadAll = useCallback(async () => {
    try {
      const [g, c, u] = await Promise.all([
        fetch(`${API}/api/staff-messages/general`, { credentials: 'include' })
          .then((r) => r.ok ? r.json() : Promise.reject(new Error(`general ${r.status}`))),
        fetch(`${API}/api/staff-messages/crew?route_date=${today}`, { credentials: 'include' })
          .then((r) => r.ok ? r.json() : Promise.reject(new Error(`crew ${r.status}`))),
        fetch(`${API}/api/staff-messages/unread-count`, { credentials: 'include' })
          .then((r) => r.ok ? r.json() : Promise.reject(new Error(`unread ${r.status}`))),
      ]);
      if (g?.messages) setGeneral(g.messages);
      if (c?.messages) setCrew(c.messages);
      if (u) setCounts(u);
      setLoadError(null);
    } catch (e) {
      setLoadError('Could not load messages — tap to retry');
      // Iter-176 · Phase 8 — retry telemetry.
      fetch(`${API}/api/telemetry/message-retry`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel: 'both', op: 'load',
                               error_type: String(e?.message || 'network') }),
      }).catch(() => {});
    }
  }, [today]);

  useEffect(() => { loadAll(); const t = setInterval(loadAll, 30000); return () => clearInterval(t); }, [loadAll]);

  const send = async (channel, body) => {
    const payload = channel === 'crew'
      ? { channel: 'crew', body, crew_id: user?.crew_id || `crew-${(user?.user_id || 'me').slice(-6)}`,
          route_date: today }
      : { channel: 'general', body };
    try {
      const r = await fetch(`${API}/api/staff-messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setSendError(err.detail || 'Message failed to send — tap to retry');
        toast.error(err.detail || 'Failed to post message');
        fetch(`${API}/api/telemetry/message-retry`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ channel, op: 'send', error_type: `http_${r.status}` }),
        }).catch(() => {});
        return;
      }
      const posted = await r.json();
      // Iter-173 · Phase 6 — optimistic prepend so the new message
      // shows instantly without waiting for the 30s poll cycle.
      if (channel === 'crew') setCrew((p) => [posted, ...p]);
      else setGeneral((p) => [posted, ...p]);
      setSendError(null);
      toast.success('Posted');
      // Follow-up refresh to sync counters.
      loadAll();
    } catch (e) {
      setSendError('Message failed to send — tap to retry');
      toast.error('Network error — try again');
      fetch(`${API}/api/telemetry/message-retry`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel, op: 'send',
                               error_type: String(e?.message || 'network') }),
      }).catch(() => {});
    }
  };

  // Iter-176 · Phase 6 — "On the way" ack. Optimistically mark the
  // message as acked in state so the pill flips immediately.
  const canAck = ['crew_leader', 'supervisor', 'manager',
                    'regional_manager', 'company_manager', 'admin']
    .includes((user?.role || '').toLowerCase());
  const ackOnTheWay = async (messageId) => {
    try {
      const r = await fetch(`${API}/api/staff-messages/${messageId}/ack-on-the-way`,
                             { method: 'POST', credentials: 'include' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not ack — try again');
        return;
      }
      const data = await r.json();
      const patched = data?.message;
      if (patched) {
        setCrew((rows) => rows.map((m) => m.message_id === messageId ? { ...m, ...patched } : m));
      }
      toast.success('Crew marked on the way');
    } catch (_) {
      toast.error('Network error');
    }
  };

  // Iter-173 · Phase 6 — auto-mark-read when a tab is opened so the
  // unread badge clears without a manual click.
  useEffect(() => {
    if (!active) return;
    fetch(`${API}/api/staff-messages/read-all?channel=${active}`,
          { method: 'POST', credentials: 'include' })
      .then(() => loadAll())
      .catch(() => {});
  }, [active, loadAll]);

  const markAll = async (channel) => {
    await fetch(`${API}/api/staff-messages/read-all?channel=${channel}`,
                  { method: 'POST', credentials: 'include' });
    await loadAll();
  };

  return (
    <Card className="p-4 bg-[#141414] border-zinc-800" data-testid="live-messages-panel">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="h-4 w-4 text-[#C9A962]" />
        <h3 className="text-base font-bold text-white">Live Messages</h3>
        {counts.total > 0 && (
          <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white"
                data-testid="live-messages-total-badge">
            {counts.total} unread
          </span>
        )}
      </div>
      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="flex gap-1 h-auto p-1">
          <TabsTrigger value="general" className="text-sm font-semibold px-4 py-2 h-10"
                       data-testid="live-messages-tab-general">
            General / Regional{counts.general > 0 ? ` (${counts.general})` : ''}
          </TabsTrigger>
          <TabsTrigger value="crew" className="text-sm font-semibold px-4 py-2 h-10"
                       data-testid="live-messages-tab-crew">
            Crew / Daily{counts.crew > 0 ? ` (${counts.crew})` : ''}
          </TabsTrigger>
        </TabsList>

        {/* Iter-175 · Phase 4 — inline visible error banners. Never
            blank/crash the panel. Tap to retry the failing call. */}
        {loadError && (
          <button
            type="button"
            onClick={() => { setLoadError(null); loadAll(); }}
            className="mt-3 w-full rounded-md border border-red-500/60 bg-red-500/10 px-3 py-2 text-left text-xs font-semibold text-red-300 hover:bg-red-500/20 transition"
            data-testid="live-messages-load-error"
          >
            {loadError}
          </button>
        )}
        {sendError && (
          <button
            type="button"
            onClick={() => setSendError(null)}
            className="mt-2 w-full rounded-md border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-left text-xs font-semibold text-amber-300 hover:bg-amber-500/20 transition"
            data-testid="live-messages-send-error"
          >
            {sendError}
          </button>
        )}

        <TabsContent value="general" className="mt-3" data-testid="live-messages-general">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-3.5 w-3.5 text-zinc-400" />
            <p className="text-xs text-zinc-400">
              Broadcast to everyone in <strong>{user?.region_name || 'your region'}</strong>. Use @name to tag.
            </p>
            {general.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => markAll('general')}
                      className="ml-auto text-[10px] uppercase tracking-widest text-zinc-400"
                      data-testid="live-messages-mark-all-general">
                Mark all read
              </Button>
            )}
          </div>
          <Composer placeholder="Post a message to your region…"
                    onSend={(b) => send('general', b)} />
          <div className="mt-4" data-testid="live-messages-list-general">
            {general.length === 0 ? (
              <p className="text-xs italic text-zinc-500 py-6 text-center">
                No regional messages yet.
              </p>
            ) : general.map((m) => <MessageRow key={m.message_id} m={m} onAck={ackOnTheWay} canAck={canAck} />)}
          </div>
        </TabsContent>

        <TabsContent value="crew" className="mt-3" data-testid="live-messages-crew">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-3.5 w-3.5 text-zinc-400" />
            <p className="text-xs text-zinc-400">
              Scoped to your crew &amp; today&apos;s route. Only your assigned crew sees this.
            </p>
            {crew.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => markAll('crew')}
                      className="ml-auto text-[10px] uppercase tracking-widest text-zinc-400"
                      data-testid="live-messages-mark-all-crew">
                Mark all read
              </Button>
            )}
          </div>
          <Composer placeholder="Message your crew for today's route…"
                    onSend={(b) => send('crew', b)} />
          <div className="mt-4" data-testid="live-messages-list-crew">
            {crew.length === 0 ? (
              <p className="text-xs italic text-zinc-500 py-6 text-center">
                No crew messages for today yet.
              </p>
            ) : crew.map((m) => <MessageRow key={m.message_id} m={m} onAck={ackOnTheWay} canAck={canAck} />)}
          </div>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
